import { useCallback } from "react";
import { useTranslation } from "react-i18next";
import {
  ApolloError,
  useMutation,
  useQuery,
  useSubscription,
} from "@apollo/client";
import { DocumentNode } from "graphql";
import type { TFunction } from "i18next";
import {
  MY_TIMESHEET_COSTS_SUBSCRIPTION,
  MY_TIMESHEET_GET_COSTS_LIST,
} from "my-timesheet/graphql";
import {
  MY_TIMESHEET_CREATE_COST,
  MY_TIMESHEET_UPDATE_COST,
} from "my-timesheet/graphql";
import { useOcSnackbar } from "oc/hooks";

export interface UseMyTimesheetCostListParams {
  page?: number;
  pageSize?: number;
  sort?: string;
  sortDirection?: string;
  searchText?: string;
  searchField?: string;
  validity?: boolean;
}

export interface CostItem {
  _id: string;
  value: number;
  [key: string]: any;
}

export interface CostList {
  rows: CostItem[];
  page?: number;
  pageSize?: number;
  total?: number;
  totalPages?: number;
  [key: string]: any;
}

interface CostListData {
  listMyTimesheetCosts: CostList;
}

interface CostListVars extends UseMyTimesheetCostListParams { }

export interface UseMyTimesheetCostListResult {
  data: CostList;
  loading: boolean;
  error?: ApolloError;
  createMySheetCost: (options?: any) => Promise<any>;
  updateMySheetCost: (options?: any) => Promise<any>;
}

export const useMyTimesheetCostList = ({
  page,
  pageSize,
  sort,
  sortDirection,
  searchText,
  searchField,
  validity,
}: UseMyTimesheetCostListParams): UseMyTimesheetCostListResult => {
  const { t }: { t: TFunction } = useTranslation();
  const { setOpenMessage } = useOcSnackbar();

  const { data, loading, error, refetch } = useQuery<
    CostListData,
    CostListVars
  >(MY_TIMESHEET_GET_COSTS_LIST as DocumentNode, {
    fetchPolicy: "cache-and-network",
    variables: {
      page,
      pageSize,
      sort,
      sortDirection,
      searchText,
      searchField,
      validity,
    },
  });

  useSubscription(MY_TIMESHEET_COSTS_SUBSCRIPTION as DocumentNode, {
    onData: () => {
      refetch();
    },
  });

  const [createMySheetCostMutation] = useMutation<any, any>(
    MY_TIMESHEET_CREATE_COST as DocumentNode,
  );

  const createMySheetCost = useCallback(
    async (options?: any) => {
      try {
        const result = await createMySheetCostMutation(options);
        setOpenMessage({
          type: "success",
          message: t("MY_TIMESHEET_NEW_COST_SUCCESSFUL_CREATED"),
        });
        refetch();
        return result;
      } catch (mutationError) {
        console.error({ mutationError });
        setOpenMessage({
          type: "error",
          message: t("MY_TIMESHEET_NEW_COST_CREATE_FAILED"),
        });
        refetch();
        throw mutationError;
      }
    },
    [createMySheetCostMutation, setOpenMessage, t, refetch],
  );

  const [updateMySheetCostMutation] = useMutation<any, any>(
    MY_TIMESHEET_UPDATE_COST as DocumentNode,
  );

  const updateMySheetCost = useCallback(
    async (options?: any) => {
      try {
        const result = await updateMySheetCostMutation(options);
        setOpenMessage({
          type: "success",
          message: t("MY_TIMESHEET_COST_SUCCESSFUL_UPDATED"),
        });
        refetch();
        return result;
      } catch (mutationError) {
        console.error({ mutationError });
        setOpenMessage({
          type: "error",
          message: t("MY_TIMESHEET_COST_UPDATE_FAILED"),
        });
        refetch();
        throw mutationError;
      }
    },
    [updateMySheetCostMutation, setOpenMessage, t, refetch],
  );

  const list = data?.listMyTimesheetCosts ?? { rows: [] };
  const parsedRows = list.rows.map((row) => ({
    ...row,
    code: row._id,
    value: row.value,
  }));

  const newData: CostList = {
    ...list,
    rows: parsedRows,
  };

  return {
    data: newData,
    loading,
    error,
    createMySheetCost,
    updateMySheetCost,
  };
};

export default useMyTimesheetCostList;
